import React from 'react';
import { motion } from 'framer-motion';
import { TabletIcon, UtensilsIcon, TrendingUpIcon, ArrowRightIcon, RefreshCwIcon, ShieldCheckIcon, SparklesIcon, CalendarClockIcon, ConciergeBellIcon } from 'lucide-react';
import { Header } from '../Header';
import { SiteFooter } from '../SiteFooter';
import { FadeIn } from '../ui';
export function HcnPage() {
  return <div className="min-h-screen bg-white font-sans text-slate-800 flex flex-col" data-id="element-4668">
      <Header data-id="element-4669" />

      <main className="flex-1" data-id="element-4670">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden bg-[#0A0F1F]" data-id="element-4671">
          {/* Ambient background */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-4672">
            <motion.div animate={{
            x: [0, 30, 0],
            y: [0, -20, 0]
          }} transition={{
            duration: 14,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-coral/[0.13] rounded-full blur-[100px]" data-id="element-4673" />
            <motion.div animate={{
            x: [0, -25, 0],
            y: [0, 30, 0]
          }} transition={{
            duration: 16,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-1/4 -right-40 w-[500px] h-[500px] bg-brand-teal/[0.15] rounded-full blur-[100px]" data-id="element-4674" />
          </div>

          {/* Grid texture */}
          <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
        }} data-id="element-4675" />

          <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10 text-center" data-id="element-4676">
            <FadeIn data-id="element-4677">
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-2 pr-4 py-1.5 mb-8 backdrop-blur-sm" data-id="element-4678">
                <span className="bg-brand-teal text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-4679">
                  Integration
                </span>
                <span className="text-sm font-medium text-slate-300" data-id="element-4680">
                  Offsite+ &amp; HCN In-Room Tablets
                </span>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-4681">
              <h1 className="text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight text-white mb-6 text-balance" data-id="element-4682">
                Your offsite, on the{' '}
                <span className="text-brand-teal" data-id="element-4683">screen in every room.</span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-4684">
              <p className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed text-balance max-w-3xl mx-auto" data-id="element-4685">
                Offsite Plus turns HCN's in-room tablets into a personalized
                concierge for every attendee — surfacing each guest's agenda,
                learning pathway, and one-tap upsells on the biggest screen in
                the room.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-4686">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-4687">
                <a href="https://calendly.com/ashsingh/post-hitec-executive-briefing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed no-underline bg-brand-teal text-white hover:bg-teal-500 focus:ring-brand-teal shadow-lg shadow-brand-teal/20 text-base px-8 py-4 group w-full sm:w-auto" data-id="element-4688">
                  Book an Integration Briefing
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4689" />
                </a>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Value Props */}
        <section className="py-24 bg-white" data-id="element-4690">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-4691">
            <FadeIn data-id="element-4692">
              <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-4693">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4 tracking-tight" data-id="element-4694">
                  The in-room tablet, reimagined for groups.
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed" data-id="element-4695">
                  HCN puts a smart tablet in every room. Offsite+ fills it with
                  personalized programming and revenue moments tuned to each
                  attendee.
                </p>
              </div>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-4696">
              <FadeIn delay={0.1} data-id="element-4697">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4698">
                  <div className="w-14 h-14 bg-brand-navy/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4699">
                    <TabletIcon className="w-7 h-7 text-brand-navy" data-id="element-4700" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4701">
                    Personalized In-Room Agenda
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4702">
                    Each guest's HCN tablet greets them by name with their own
                    Offsite+ schedule, learning pathway, and pod assignments —
                    no app download, no friction.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-4703">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4704">
                  <div className="w-14 h-14 bg-brand-coral/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4705">
                    <UtensilsIcon className="w-7 h-7 text-brand-coral" data-id="element-4706" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4707">
                    One-Tap Ordering &amp; Upsells
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4708">
                    Room service, spa, and activity upsells surface on the
                    tablet at the right moment in the offsite — converting
                    downtime into ancillary revenue with a single tap.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.3} data-id="element-4709">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4710">
                  <div className="w-14 h-14 bg-brand-teal/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4711">
                    <TrendingUpIcon className="w-7 h-7 text-brand-teal" data-id="element-4712" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4713">
                    A New Revenue Channel
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4714">
                    Turn the in-room screen into a high-visibility merchandising
                    surface for group programming, sponsor content, and premium
                    experiences — with every conversion measured.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Technical Integration */}
        <section className="py-24 bg-slate-50 border-t border-slate-100" data-id="element-4715">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-4716">
            <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-4717">
              <FadeIn data-id="element-4718">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-6 tracking-tight" data-id="element-4719">
                  Synced to the room, tuned to the schedule.
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-4720">
                  Offsite+ knows who's in which room and what's next on their
                  agenda — and HCN renders it beautifully on the in-room tablet,
                  updating live as the offsite unfolds.
                </p>

                <ul className="space-y-6" data-id="element-4721">
                  {[{
                  icon: RefreshCwIcon,
                  title: 'Room-Level Personalization',
                  desc: 'Attendee-to-room mapping delivers the right agenda and upsells to the right tablet.'
                }, {
                  icon: CalendarClockIcon,
                  title: 'Schedule-Aware Content',
                  desc: 'The tablet updates in real time as sessions, breaks, and activities move through the day.'
                }, {
                  icon: ShieldCheckIcon,
                  title: 'Secure & Privacy-First',
                  desc: 'Guest data is handled with encryption and clears automatically at checkout.'
                }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-4722">
                      <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-4723">
                        <item.icon className="w-5 h-5 text-brand-teal" data-id="element-4724" />
                      </div>
                      <div data-id="element-4725">
                        <h4 className="text-base font-bold text-brand-navy mb-1" data-id="element-4726">
                          {item.title}
                        </h4>
                        <p className="text-sm text-slate-600 leading-relaxed" data-id="element-4727">
                          {item.desc}
                        </p>
                      </div>
                    </li>)}
                </ul>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-4728">
                <div className="relative" data-id="element-4729">
                  <div className="absolute inset-0 bg-gradient-to-tr from-brand-teal/20 to-brand-coral/20 rounded-3xl blur-3xl transform rotate-3" data-id="element-4730" />
                  {/* In-room tablet mock (landscape) */}
                  <div className="relative bg-slate-900 rounded-[2rem] p-3 shadow-2xl border border-slate-800" data-id="element-4731">
                    <div className="bg-white rounded-[1.5rem] overflow-hidden" data-id="element-4732">
                      <div className="bg-brand-navy text-white px-6 py-5 flex items-center justify-between" data-id="element-4733">
                        <div data-id="element-4734">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-brand-teal mb-0.5" data-id="element-4735">
                            Powered by Offsite+ · Room 412
                          </div>
                          <div className="text-lg font-bold leading-tight" data-id="element-4736">
                            Good morning, Sarah 👋
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-brand-teal bg-white/10 px-2 py-1 rounded-full" data-id="element-4737">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-pulse" data-id="element-4738" />
                          Live
                        </div>
                      </div>

                      <div className="p-5 grid grid-cols-2 gap-3" data-id="element-4739">
                        <div className="col-span-2 bg-slate-50 rounded-xl border border-slate-100 p-4 flex items-center gap-3" data-id="element-4740">
                          <div className="w-10 h-10 rounded-lg bg-brand-navy/10 flex items-center justify-center shrink-0" data-id="element-4741">
                            <CalendarClockIcon className="w-5 h-5 text-brand-navy" data-id="element-4742" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-4743">
                            <div className="text-sm font-bold text-brand-navy" data-id="element-4744">
                              Next: Leadership Reset · 9:00 AM
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-4745">
                              Palm Courtyard · Pod 3
                            </div>
                          </div>
                        </div>

                        <div className="bg-brand-coral/10 border border-brand-coral/20 rounded-xl p-4" data-id="element-4746">
                          <div className="w-8 h-8 rounded-lg bg-brand-coral/15 flex items-center justify-center mb-2" data-id="element-4747">
                            <ConciergeBellIcon className="w-4 h-4 text-brand-coral" data-id="element-4748" />
                          </div>
                          <div className="text-sm font-bold text-brand-navy leading-tight mb-0.5" data-id="element-4749">
                            In-room breakfast
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-4750">
                            Order in one tap
                          </div>
                        </div>

                        <div className="bg-brand-teal/10 border border-brand-teal/20 rounded-xl p-4" data-id="element-4751">
                          <div className="w-8 h-8 rounded-lg bg-brand-teal/15 flex items-center justify-center mb-2" data-id="element-4752">
                            <SparklesIcon className="w-4 h-4 text-brand-teal" data-id="element-4753" />
                          </div>
                          <div className="text-sm font-bold text-brand-navy leading-tight mb-0.5" data-id="element-4754">
                            Sunset wellness
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-4755">
                            Add for $45
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-white text-center" data-id="element-4756">
          <div className="max-w-3xl mx-auto px-6 md:px-12" data-id="element-4757">
            <FadeIn data-id="element-4758">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-6" data-id="element-4759">
                Ready to put your offsite in every room?
              </h2>
              <p className="text-lg text-slate-600 mb-10" data-id="element-4760">
                Let's walk through how the Offsite+ and HCN integration turns
                your in-room tablets into a personalized, revenue-generating
                companion for every corporate group.
              </p>
              <a href="https://calendly.com/ashsingh/post-hitec-executive-briefing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 no-underline bg-brand-navy text-white hover:bg-slate-800 focus:ring-brand-navy shadow-lg text-base px-8 py-4 group" data-id="element-4761">
                Schedule a Demo
                <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4762" />
              </a>
            </FadeIn>
          </div>
        </section>
      </main>

      <SiteFooter data-id="element-4763" />
    </div>;
}